import SaveOutlined from '@ant-design/icons/SaveOutlined'
import { Button, Typography } from 'antd'
import {
  EnrichedOntologyFieldDefinition,
  OntologyField,
  useOntology,
} from 'features/dynamicOntology'
import { RolodexDocumentEntry, TaggedDocumentField } from 'features/files/types'
import { useGeckoMessages, useGeckoNotifications } from 'features/notifications'
import { useSageService } from 'features/sage'
import { Sparkles } from 'lucide-react'
import * as React from 'react'
import { useCallback, useEffect } from 'react'
import styled from 'styled-components'

const { Text } = Typography

/**
 * Builds the field extraction prompt for the LLM service
 */
const buildFieldExtractionPrompt = (
  documentName: string,
  defaultKeys: ReadonlyArray<string> = [],
  ontologyFields: Record<OntologyField, EnrichedOntologyFieldDefinition>
): string => {
  const defaultKeysText =
    defaultKeys.length > 0
      ? `\n\nPlease extract the following fields if they exist in the document: ${defaultKeys.join(', ')}`
      : ''

  return `
You're an expert mechanical engineer and document analyzer with 
a deep understanding of industrial equipment, technical specifications, and engineering documents.

Extract key information from this document as key-value pairs.

Document: ${documentName}

${defaultKeysText}

Fields should conform with the ontology where possible: ${JSON.stringify(ontologyFields)}, eg if looking at a unit's document and it is insulated, return only a boolean for insulated.

Return the results as a JSON object with key-value pairs. Only include fields that have actual values found in the document.
Example format:
{
  "design_pressure_bar": "10.5",
  "operating_temperature_c": "150",
  "material": "Carbon Steel",
  "inspection_interval_yrs": "5"
}

If no relevant technical information is found, return an empty object: {}
`
}

/**
 * Props for document field extractor component
 */
export interface DocumentAiFieldExtractorProps {
  onApply: (fields: ReadonlyArray<TaggedDocumentField>) => void
  document?: RolodexDocumentEntry
  defaultKeys?: string[]
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  margin-bottom: 16px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`

const ExtractButton = styled(Button)`
  font-weight: 600;
  height: 36px;
  border-radius: 6px;
`

const ApplyButton = styled(Button)`
  font-weight: 600;
  height: 36px;
  border-radius: 6px;
`

const ExtractedFieldsContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 6px;
`

const FieldItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};

  &:last-child {
    border-bottom: none;
  }
`

const FieldKey = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
`

const FieldValue = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-family: monospace;
`

export const DocumentAiFieldExtractor: React.FC<
  DocumentAiFieldExtractorProps
> = ({ onApply, document, defaultKeys }) => {
  const { fetchVertexQuery } = useSageService()
  const [isExtracting, setIsExtracting] = React.useState(false)
  const [extractedFields, setExtractedFields] = React.useState<
    ReadonlyArray<TaggedDocumentField>
  >([])
  const { warningNotification } = useGeckoNotifications()
  const ontology = useOntology()
  const { errorMessage, infoMessage, successMessage } = useGeckoMessages()

  const handleExtractFields = useCallback(
    async (autoApply = false) => {
      const rolodexFileRef = document?.rolodex_file_ref

      if (!rolodexFileRef) {
        errorMessage(
          `No file available for extraction. Document: ${document?.filename}`
        )
        warningNotification('No file available for extraction')
        return
      }

      setIsExtracting(true)
      try {
        const apiResponse = await fetchVertexQuery({
          rolodexFiles: [
            {
              id: rolodexFileRef.entry_id,
              path: '/' + rolodexFileRef.path,
            },
          ],
          query: buildFieldExtractionPrompt(
            document.filename || 'Unknown Document',
            defaultKeys,
            ontology.fields
          ),
        })

        const responseText = apiResponse.result.trim()

        // Try to parse the response as JSON
        let parsedFields: Record<string, string> = {}
        try {
          // Remove any markdown code blocks if present
          const jsonMatch = responseText.match(
            /```(?:json)?\s*(\{.*?\})\s*```/s
          )
          const jsonText = jsonMatch ? jsonMatch[1] : responseText
          parsedFields = JSON.parse(jsonText)
        } catch (parseError) {
          console.error('Failed to parse AI response as JSON:', parseError)
          errorMessage('Failed to parse extracted fields. Please try again.')
          return
        }

        const fields: ReadonlyArray<TaggedDocumentField> = Object.entries(
          parsedFields
        )
          .filter(([key, value]) => key && value && typeof value === 'string')
          .map(([key, value]) => ({
            key: key.trim(),
            value: String(value).trim(),
          }))

        setExtractedFields(fields)

        if (fields.length === 0) {
          infoMessage('No relevant fields found in the document')
        } else {
          successMessage(
            `Successfully extracted ${fields.length} fields from document`
          )

          // Auto-apply if this was triggered automatically
          if (autoApply && fields.length > 0) {
            onApply(fields)
            successMessage('Automatically applied extracted fields')
          }
        }
      } catch (error) {
        console.error('Error extracting fields:', error)
        errorMessage(
          'Failed to extract fields from document. Please try again.'
        )
      } finally {
        setIsExtracting(false)
      }
    },
    [
      document?.rolodex_file_ref,
      document?.filename,
      warningNotification,
      fetchVertexQuery,
      defaultKeys,
      onApply,
      ontology.fields,
      errorMessage,
      infoMessage,
      successMessage,
    ]
  )

  const handleApplyFields = useCallback(() => {
    if (extractedFields.length > 0) {
      onApply(extractedFields)
      successMessage('Applied extracted fields to document')
    }
  }, [extractedFields, onApply, successMessage])

  useEffect(() => {
    // When the document changes, clear previous extraction
    setExtractedFields([])
    setIsExtracting(false)
  }, [document?.entry_id])

  return (
    <Container>
      <Text strong style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
        AI Field Extraction
      </Text>

      <Text type='secondary' style={{ marginBottom: 12, display: 'block' }}>
        Use AI to automatically extract technical information and fields from
        this document.
      </Text>

      <ButtonGroup>
        <ExtractButton
          type='default'
          onClick={() => handleExtractFields(false)}
          loading={isExtracting}
          disabled={isExtracting}
          icon={<Sparkles size={16} />}>
          Extract Fields
        </ExtractButton>

        {extractedFields.length > 0 && (
          <ApplyButton
            type='default'
            onClick={handleApplyFields}
            disabled={isExtracting}
            icon={<SaveOutlined />}>
            Save AI Extracted Fields ({extractedFields.length})
          </ApplyButton>
        )}
      </ButtonGroup>

      {extractedFields.length > 0 && (
        <ExtractedFieldsContainer>
          <Text strong style={{ marginBottom: 8, display: 'block' }}>
            Extracted Fields:
          </Text>
          {extractedFields.map((field, index) => (
            <FieldItem key={index}>
              <FieldKey>{field.key}:</FieldKey>
              <FieldValue>{field.value}</FieldValue>
            </FieldItem>
          ))}
        </ExtractedFieldsContainer>
      )}
    </Container>
  )
}
